import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet.markercluster"
import "leaflet.zoomhome"
import "@maplibre/maplibre-gl-leaflet"
import { setWorkerUrl } from "maplibre-gl"

// Connects to data-controller="map"
//
// Configurable via values:
//   places       - Array of place objects to render (used when not fetching)
//   cluster      - Boolean, group markers with leaflet.markercluster
//   fetchPlaces  - Boolean, fetch places from /places.json (shows a spinner)
export default class extends Controller {
  static values = {
    places: Array,
    cluster: Boolean,
    fetchPlaces: Boolean
  }

  async connect() {
    this.initializeBaseMap()

    if (this.fetchPlacesValue) {
      await this.loadSpinner()
      this.startSpinner()
      this.placesProvider()
        .then(places => {
          this.stopSpinner()
          this.addPlaces(places)
        })
        .catch(err => {
          this.stopSpinner()
          console.error("placesProvider failed", err)
        })
    } else {
      this.addPlaces(this.placesValue || [])
    }

    this.enableEditMarker()
  }

  disconnect() {
    this.stopSpinner()
    this.destroyMapIfExists()
  }

  // --- Spinner (used when fetching places asynchronously) ---
  async loadSpinner() {
    try {
      const spinMod = await import("spin.js")
      this.Spinner = spinMod.default || spinMod.Spinner || spinMod
    } catch (e) {
      console.warn("Failed to load spin.js", e)
    }
  }

  startSpinner() {
    const map = window.map
    if (!map || !this.Spinner) return

    const container = map.getContainer()
    if (container && (!container.style.position || container.style.position === "static")) {
      container.style.position = "relative"
    }

    this._spinner = new this.Spinner(this.spinnerOptions()).spin(container)
  }

  stopSpinner() {
    if (this._spinner) {
      this._spinner.stop()
      this._spinner = null
    }
  }

  spinnerOptions() {
    return {
      lines: 13,
      length: 28,
      width: 14,
      radius: 42,
      scale: 1,
      corners: 1,
      color: "#000",
      opacity: 0.25,
      rotate: 0,
      direction: 1,
      speed: 1,
      trail: 60,
      fps: 20,
      // zIndex: 2e9,
      className: "spinner",
      top: "50%",
      left: "50%",
      shadow: false,
      hwaccel: false,
      position: "absolute"
    }
  }

  // --- Map setup ---
  destroyMapIfExists() {
    if (window.map && typeof window.map.remove === "function") {
      window.map.remove()
      window.map = null
    }
    window.markers = null
    window.zoomHome = null
  }

  initializeBaseMap() {
    const mapElement = this.element.querySelector("#map")
    if (!mapElement) return

    setWorkerUrl(mapElement.dataset.maplibreWorkerUrl)

    const defaultView = this.defaultView(mapElement)
    window.map = L.map(mapElement, {
      center: defaultView.center,
      minZoom: 2,
      maxZoom: 19,
      scrollWheelZoom: false,
      zoom: defaultView.zoom,
      zoomControl: false
    })

    const mapMarker = L.icon({
      iconUrl: mapElement.dataset.iconUrl,
      iconRetinaUrl: mapElement.dataset.iconRetinaUrl,
      shadowUrl: mapElement.dataset.shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41]
    })
    L.Marker.prototype.options.icon = mapMarker

    L.maplibreGL({
      style: 'https://tiles.stadiamaps.com/styles/outdoors.json',
      attribution: '&copy; <a href="https://stadiamaps.com/" target="_blank">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/about" target="_blank">OpenStreetMap</a> contributors',
    }).addTo(window.map)

    window.markers = this.initializeMarkers()
    window.markers.addTo(window.map)
    window.zoomHome = L.Control.zoomHome()
    window.zoomHome.addTo(window.map)
  }

  initializeMarkers() {
    if (this.clusterValue) {
      return new L.markerClusterGroup({
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        maxClusterRadius: 40
      })
    }
    return new L.LayerGroup()
  }

  defaultView(mapElement = this.element.querySelector("#map")) {
    const scope = (mapElement?.dataset?.countryScope || "US").toUpperCase()
    if (scope === "CA") {
      return { center: [60.0, -96.0], zoom: 3 }
    }
    return { center: [39.5, -99.7], zoom: 3 }
  }

  placesProvider() {
    const placesPath = "/places.json" + window.location.search
    return fetch(placesPath, { method: "GET" }).then(response => response.json())
  }

  addPlaces(places) {
    const map = window.map
    const markers = window.markers
    if (!map || !markers) return

    if (markers.clearLayers) markers.clearLayers()

    if (!places || places.length === 0) {
      console.warn("No places to display on map")
      const defaultView = this.defaultView()
      map.setView(defaultView.center, defaultView.zoom + 1)
      return
    }

    const bounds = []
    const markerArray = []

    for (const place of places) {
      const marker = L.marker(place.latlng)
      // Defer popup binding until the marker is clicked
      marker.on("click", () => {
        if (!marker.getPopup() && place.popup) {
          marker.bindPopup(place.popup).openPopup()
        }
      })
      markerArray.push(marker)
      bounds.push(place.latlng)
    }

    if (typeof markers.addLayers === "function") {
      markers.addLayers(markerArray)
    } else {
      markerArray.forEach(marker => markers.addLayer(marker))
    }

    let center = null
    let zoom = null
    if (places.length === 1) {
      center = places[0].latlng
      zoom = places[0].zoom
    }

    this.setMapView(map, bounds, center, zoom)
  }

  setMapView(map, bounds, center, zoom) {
    if (!window.zoomHome) return
    if (center && zoom) {
      map.setView(center, zoom)
      window.zoomHome.setHomeCoordinates(center)
      window.zoomHome.setHomeZoom(zoom)
    } else if (bounds.length) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 })
      window.zoomHome.setHomeBounds(bounds)
    }
  }

  // --- Marker editing logic for edit_map ---
  enableEditMarker() {
    // Only enable if the edit form is present
    const latInput = document.getElementById("location_latitude")
    const lngInput = document.getElementById("location_longitude")
    const zoomInput = document.getElementById("location_zoom")
    if (!latInput || !lngInput || !zoomInput || !window.map) return

    window.map.on("click", event => {
      this.clearMarkers(window.map)
      this.createMarker(window.map, event)
      this.updateFormLocation(window.map, event)
    })

    window.map.on("zoom", () => {
      this.updateFormZoom(window.map)
    })
  }

  clearMarkers(map) {
    if (window.markers) {
      map.removeLayer(window.markers)
      window.markers = new L.LayerGroup()
    }
  }

  createMarker(map, e) {
    const marker = L.marker(e.latlng).addTo(window.markers)
    window.markers.addTo(map)
    return marker
  }

  updateFormLocation(map, event) {
    document.getElementById("location_latitude").value = event.latlng.lat.toFixed(5)
    document.getElementById("location_longitude").value = event.latlng.lng.toFixed(5)
    this.updateFormZoom(map)
  }

  updateFormZoom(map) {
    document.getElementById("location_zoom").value = map.getZoom()
  }
}
